import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import Link from '@atlaskit/link/link';
import Checkbox from '@atlaskit/checkbox';
import FeedbackCollector from '@atlaskit/feedback-collector';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { usePageRestrictionsContext } from '@confluence/page-restrictions-context/entry-points/PageRestrictionsContext.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { HandleFeedbackSubmissionArgs } from '../useEditorPluginAI.ts';

const GAIE_ENTRYPOINT_ID = '73cf1eae-42a3-418a-a7ac-976ba0b5735d';
const FEEDBACK_CONTEXT_ID = 'customfield_10047';
const LINK_TO_PRIVACY_POLICY = 'https://www.atlassian.com/legal/privacy-policy';
const LINK_TO_RESEARCH_GROUP = 'https://www.atlassian.com/research-group';

const i18n = defineMessages({
	feedbackTitle: {
		id: 'editor-features.rovo-feedback-collector.title',
		defaultMessage: 'Help us make Rovo even better',
		description: 'Title of feedback collector used to get feedback for Editor AI features',
	},
	flagTitle: {
		id: 'editor-features.ai-feedback-collector.flag-title',
		defaultMessage: 'Thanks!',
		description:
			'The text is shown as the title of a success flag notification that appears after the user submits feedback on an AI-generated result in the editor.',
	},
	flagDescription: {
		id: 'editor-features.ai-feedback-collector.flag-description',
		defaultMessage: 'Your valuable feedback helps us continually improve our products.',
		description: 'Description text for the feedback sent flag',
	},
	summaryPlaceholder: {
		id: 'editor-features.ai-feedback-collector.summary-placeholder',
		defaultMessage: 'Let us know what you were trying to do and how it could be better',
		description: 'Placeholder text inside input box of feedback collector.',
	},
	userConsentCheckboxLabel: {
		id: 'editor-features.ai-feedback-collector.user-consent-checkbox-label',
		defaultMessage: 'Include my last prompt and response in the feedback.',
		description:
			'Label for the checkbox asking for user consent to include their AI prompt and response in their feedback',
	},
	canContactText: {
		id: 'editor-features.ai-feedback-collector.can-contact.label',
		defaultMessage: 'Atlassian can contact me about this feedback. See our {privacyPolicyLink}.',
		description:
			"Text next to first checkbox. {privacyPolicyLink} will be substituted with a message which reads as 'privacy policy'",
	},
	privacyPolicyLink: {
		id: 'editor-features.ai-feedback-collector.privacy-policy.link',
		defaultMessage: 'privacy policy',
		description: 'The text of the link which will help user go to the privacy policy site.',
	},
	improveAtlassianText: {
		id: 'editor-features.ai-feedback-collector.improve-atlassian',
		defaultMessage:
			'I’d like to help improve Atlassian products by joining the {researchGroupLink}.',
		description:
			"Text next to second checkbox. {researchGroupLink} will be substituted with a message which reads as 'Atlassian Research Group'",
	},
	researchGroupLink: {
		id: 'editor-features.ai-feedback-collector.research-group.link',
		defaultMessage: 'Atlassian Research Group',
		description: 'The text of the link which will help user go to the research group site.',
	},
});

const PrivacyPolicyLink = ({ link }) => (
	<Link target="_blank" href={link}>
		<FormattedMessage {...i18n.privacyPolicyLink} />
	</Link>
);

const ResearchGroupLink = ({ link }) => (
	<Link target="_blank" href={link}>
		<FormattedMessage {...i18n.researchGroupLink} />
	</Link>
);

const CanContactLabel = () => {
	return (
		<FormattedMessage
			{...i18n.canContactText}
			values={{
				privacyPolicyLink: <PrivacyPolicyLink link={LINK_TO_PRIVACY_POLICY} />,
			}}
		/>
	);
};

const EnrolLabel = () => {
	return (
		<FormattedMessage
			{...i18n.improveAtlassianText}
			values={{
				researchGroupLink: <ResearchGroupLink link={LINK_TO_RESEARCH_GROUP} />,
			}}
		/>
	);
};

const buildAdditionalFields = ({
	feedbackContext,
	hasUserConsent,
}: {
	feedbackContext: HandleFeedbackSubmissionArgs | undefined;
	hasUserConsent: boolean;
}) => {
	if (!feedbackContext) return undefined;
	const aiExperience = feedbackContext.getAIExperience?.(hasUserConsent) || {};
	let feedbackMetadataStringified = `sentiment: ${feedbackContext.sentiment}\n`;

	Object.entries({
		...aiExperience,
		...feedbackContext.editorAttributes,
	}).forEach((item) => {
		feedbackMetadataStringified += `${item[0]}: ${item[1]}\n`;
	});
	return [{ id: FEEDBACK_CONTEXT_ID, value: feedbackMetadataStringified }];
};

const EditorAIFeedbackCollectorComponent = ({
	flags,
	feedbackTitle,
	feedbackContext,
	onClose,
}: {
	flags: FlagsStateContainer;
	feedbackTitle?: string;
	feedbackTitleDetails?: any;
	summaryPlaceholder?: any;
	feedbackContext?: HandleFeedbackSubmissionArgs;
	onClose?: () => void;
}) => {
	const { formatMessage } = useIntl();
	const { userIdForAnalytics, displayName } = useSessionData();
	const { loading, error, restrictions } = usePageRestrictionsContext();
	const [hasUserConsent, setHasUserConsent] = useState(false);

	useEffect(() => {
		/**
		 * Once the check for page restrictions is no longer loading and has no errors,
		 * set the user content value based on the levels of restrictions for the page.
		 */

		if (loading || error || !restrictions) {
			return;
		}

		const isRestricted =
			restrictions.hasRestrictions ||
			restrictions.hasViewRestrictions ||
			restrictions.hasInheritedRestrictions;

		return setHasUserConsent(!isRestricted);
	}, [loading, error, restrictions]);

	const handleHasUserConsent = useCallback(() => {
		setHasUserConsent((current) => !current);
	}, []);

	const { showFlag } = flags;
	const displayFlag = () => {
		void showFlag({
			type: 'success-circle',
			title: formatMessage(i18n.flagTitle),
			description: formatMessage(i18n.flagDescription),
		});
	};

	return (
		<FeedbackCollector
			name={displayName ?? undefined}
			atlassianAccountId={userIdForAnalytics ?? undefined}
			onClose={onClose}
			onSubmit={displayFlag}
			customContent={
				<Checkbox
					label={formatMessage(i18n.userConsentCheckboxLabel)}
					onChange={handleHasUserConsent}
					isChecked={hasUserConsent}
				/>
			}
			entrypointId={GAIE_ENTRYPOINT_ID}
			showTypeField={false}
			feedbackTitle={feedbackTitle || formatMessage(i18n.feedbackTitle)}
			anonymousFeedback={false}
			summaryPlaceholder={formatMessage(i18n.summaryPlaceholder)}
			canBeContactedLabel={<CanContactLabel />}
			enrolInResearchLabel={<EnrolLabel />}
			additionalFields={buildAdditionalFields({
				feedbackContext,
				hasUserConsent,
			})}
		/>
	);
};

const EditorAIFeedbackCollectorWithFlags = withFlags(EditorAIFeedbackCollectorComponent);

export const useEditorAIFeedbackCollector = (): {
	FeedbackCollector: React.FC;
	openFeedbackCollector: (props: HandleFeedbackSubmissionArgs) => void;
} => {
	const [isOpen, setIsOpen] = useState(false);
	const [feedbackContext, setFeedbackContext] = useState<HandleFeedbackSubmissionArgs>();
	const onClose = useCallback(() => {
		setFeedbackContext(undefined);
		setIsOpen(false);
	}, []);

	const openFeedbackCollector = useCallback(
		(feedbackSubmissionArgs: HandleFeedbackSubmissionArgs): void => {
			setFeedbackContext(feedbackSubmissionArgs);
			setIsOpen(true);
		},
		[],
	);

	const EditorAIFeedbackCollector = useCallback(
		() =>
			isOpen ? (
				<EditorAIFeedbackCollectorWithFlags feedbackContext={feedbackContext} onClose={onClose} />
			) : null,
		[isOpen, onClose, feedbackContext],
	);

	return {
		FeedbackCollector: EditorAIFeedbackCollector,
		openFeedbackCollector,
	};
};
